import React from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { TransitionGroup } from 'react-transition-group';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';

import ToasterAlert from '../../components/Donation/ToasterAlert';
import { TransitionContainerNoExit } from '../../components/Transition/TranstionContainer';
import CssTransition from '../../components/Transition/CssTransition';
import TransitionGroupContainer from '../../components/Transition/TransitionGroupContainer';
import BounceModal from '../../components/Modals/Bounce/Bounce';

import ActionChoiceCard from '../ActionChoiceCard/ActionChoiceCard';
import Call from '../Call/Call';
import CheckRegistration from '../CheckRegistration/CheckRegistration';
import Donation from '../Donation/Donation';
import ElectionReminder from '../ElectionReminder/ElectionReminder';
import Email from '../Email/Email';
import FindPollingStation from '../FindPollingStation/FindPollingStation';
import Link from '../Link/Link';
import MultiActionsCard from '../MultiActionsCard/MultiActionsCard';
import Opinion from '../Opinion/Opinion';
import Petition from '../Petition/Petition';
import Poll from '../Poll/Poll';
import PreloadScreen from '../PreloadScreen/PreloadScreen';
import Quiz from '../Quiz/Quiz';
import RecordVideo from '../RecordVideo/RecordVideo';
import RepStance from '../RepStance/RepStance';
import RadialSlider from '../RadialSlider/RadialSlider';
import SentimentPoll from '../SentimentPoll/SentimentPoll';
import TargetedMultiAction from '../TargetedMultiAction/TargetedMultiAction';
import Tweet from '../Tweet/Tweet';
import Video from '../Video/Video';
import Volunteer from '../Volunteer/Volunteer';
import VoteRegistration from '../VoteRegistration/VoteRegistration';

import {
    resizeWidget,
    widgetBounce,
    widgetEngaged,
    widgetLoaded,
    widgetVisible
} from './actions';
import { loadLocalRep } from '../LocalRep/actions';

import { isBounceableRoute } from '../../utils/bounce';
import { ActionTypes, CardTypes } from '../../utils/enums';
import { isPortrait, height, width } from '../../utils/size';
import { findFormByPathname } from '../../utils/formCaching/helpers';

import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { FacebookPixel, initializePixels } from '../../utils/facebookPixel';
import { addEventListener, messageEvent } from '../../utils/iframeCommunication';

import { GlobalStyle,IEStyle } from './Widget.styles';
import { detect } from '../../utils/detect-browser';

import { capitalization, copyFor } from '../../copy/copy';
import ThemeContext from '../../utils/theming';
import { colorPlaceholderText } from '../../utils/byobUtils';

const WidgetContainer = styled.div`
    max-width: ${() => width()}px;
    border-top: 2px solid #dbdbdb;
    ${({ displayServiceFooter }) =>
        displayServiceFooter && `border-bottom: 2px solid #dbdbdb;`}
    box-sizing: border-box;
    font-size: 16px;
    font-family: Averta;
    display: flex;
    position: relative;
    border-bottom: 2px solid #dbdbdb;
    height: ${({ displayServiceFooter }) => height(displayServiceFooter)}px;
    background-color: white;
`;

const FooterNotice = styled.div`
    position: absolute;
    z-index: 4;
    border-top: 2px solid #dbdbdb;
    background-color: #fff;
    display: flex;
    align-items: center;
    width: 100%;
    height: ${({ isPortrait }) => (isPortrait ? 32 : 24)}px;
    bottom: ${({ isPortrait }) => (isPortrait ? -32 : -24)}px;

    a {
        color: #008eff;
        text-decoration: none;
    }
    span {
        padding: 0 1em;
        font-size: 10px;
    }
`;

//This returns a childFactory to provide to TransitionGroup
const childFactoryCreator = classNames => child =>
    React.cloneElement(child, {
        classNames
    });

const getDefaultRoute = (actionType, cardType, skipFrontCard) => {
    if (cardType === CardTypes.oneAction) {
        if (actionType === 'volunteer') {
            return 'preload/volunteer';
        }
        if (skipFrontCard) {
            return actionType;
        }
        return actionType + '/start';
    } else if (cardType === CardTypes.actionChoice) {
        return 'actionChoiceCard';
    } else if (
        cardType === CardTypes.multipleActions ||
        cardType === CardTypes.multiDonation
    ) {
        return 'multiActionsCard';
    } else if (cardType === CardTypes.targetedMultiAction) {
        return 'targetedMultiAction';
    }
};

const isPreviousActionTransition = (
    pathname,
    lastActionIdentifier,
    currentActionIdentifier,
    emailStep2BackButtonClicked,
    showNextActionAnimation
) => {
    let isPreviousActionTransition =
        (lastActionIdentifier === currentActionIdentifier &&
            (pathname === '/targetedMultiAction' ||
                pathname === '/multiActionsCard' ||
                pathname === '/actionChoiceCard' ||
                emailStep2BackButtonClicked)) ||
        showNextActionAnimation === false;
    return isPreviousActionTransition;
};

class Widget extends React.Component {
    static propTypes = {
        actionIdentifier: PropTypes.string.isRequired,
        activeAction: PropTypes.object.isRequired,
        activeActionUsesLocalReps: PropTypes.bool.isRequired,
        address: PropTypes.object,
        animationIdentifier: PropTypes.string,
        articleUrl: PropTypes.string.isRequired,
        bounce: PropTypes.bool,
        bounceFormSuccess: PropTypes.bool,
        currentFormValues: PropTypes.object,
        defaultRoute: PropTypes.string.isRequired,
        displayServiceFooter: PropTypes.bool.isRequired,
        emailStep2BackButtonClicked: PropTypes.bool,
        endUserLocation: PropTypes.object,
        loaded: PropTypes.bool,
        engaged: PropTypes.bool,
        facebookPixelIds: PropTypes.array,
        history: PropTypes.object.isRequired,
        isLocalRepLoading: PropTypes.bool,
        isNextActionTransition: PropTypes.bool,
        lastInitiatedLocalRepIdentifier: PropTypes.string,
        loadLocalRep: PropTypes.func.isRequired,
        localRepFound: PropTypes.bool.isRequired,
        locationKey: PropTypes.string,
        maximumRepresentativeSearch: PropTypes.shape({
            levels: PropTypes.array.isRequired,
            roles: PropTypes.array.isRequired
        }),
        nextAction: PropTypes.object,
        nextActionUseLocalReps: PropTypes.bool,
        pathname: PropTypes.string,
        resizeWidget: PropTypes.func,
        showNextActionAnimation: PropTypes.bool,
        themeData: PropTypes.object,
        widgetBounce: PropTypes.func.isRequired,
        widgetEngaged: PropTypes.func.isRequired,
        widgetRequestId: PropTypes.string.isRequired,
        widgetViewed: PropTypes.bool,
        widgetLoaded: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);
        this.state = {loadedRepsForSaveRoute: false};
        this.watchForWidgetVisibleEvent(props);
        const widgetReference = this;
        if (typeof window !== 'undefined') {
            window.onresize = function() {
                widgetReference.forceUpdate();
                props.resizeWidget();
            };
            props.resizeWidget();
        }
        initializePixels(props.facebookPixelIds || [], this.props.widgetId);
    }

    prefetchReps() {
        if (
            !this.props.activeActionUsesLocalReps &&
            !this.props.localRepFound &&
            !!this.props.nextAction &&
            this.props.nextActionUseLocalReps &&
            !!this.props.maximumRepresentativeSearch &&
            !this.props.isLocalRepLoading
        ) {
            this.props.loadLocalRep(
                this.props.endUserLocation,
                this.props.address,
                this.props.maximumRepresentativeSearch,
                false
            );
        }
    }

    watchForWidgetVisibleEvent(props) {
        const onWidgetVisibleEvent = e => {
            if (
                typeof window !== 'undefined' &&
                (e.data == null ||
                    e.data.isActionButtonWidgetVisible == null ||
                    e.data.isActionButtonWidgetVisible !== true)
            )
                return;

            window.removeEventListener(messageEvent, onWidgetVisibleEvent, false);
            props.widgetVisible(props.widgetRequestId, props.articleUrl);
        };

        addEventListener(messageEvent, onWidgetVisibleEvent, false);
    }

    watchForBounceEvent() {
        const onBounceEvent = e => {
            if (
                typeof window !== 'undefined' &&
                (e.data || {}).bounce === true &&
                this.props.engaged
            ) {
                if (isBounceableRoute(this.props.pathname)) {
                    window.removeEventListener(messageEvent, onBounceEvent, false);
                    this.props.widgetBounce(
                        true,
                        this.props.widgetRequestId,
                        this.props.articleUrl,
                        this.props.currentFormValues,
                        this.props.actionIdentifier
                    );
                }
            }
        };
        addEventListener(messageEvent, onBounceEvent, false);
    }

    componentDidMount() {
        if (!this.props.loaded) this.props.widgetLoaded();
        // ensures initial render === what's defined in redux state
        this.props.history.replace(this.props.defaultRoute);
        if (window.location.hash === '#isByobPreview') setTimeout(colorPlaceholderText,500);
    }

    componentDidUpdate(prevProps) {
        if (!this.state.loadedRepsForSaveRoute && this.props.savedRoute){
            this.props.loadLocalRep(
                this.props.endUserLocation,
                this.props.address,
                this.props.maximumRepresentativeSearch,
                false,
                this.props.savedRoute.includes('email') || this.props.savedRoute.includes('recordvideo')
            );
            this.setState({loadedRepsForSaveRoute: true});
        }
        else if (this.props.locationKey !== prevProps.locationKey) {
            this.prefetchReps();
        }
        if (this.props.engaged !== prevProps.engaged) {
            this.watchForBounceEvent();
        }
    }

    render() {
        return (
            <ThemeContext.Provider value={this.props.themeData}>
                <WidgetContainer
                    displayServiceFooter={this.props.displayServiceFooter}
                    onClick={() => this.props.widgetEngaged(this.props.engaged)}
                >
                    <FacebookPixel />
                    <GlobalStyle theme={this.props.themeData} />
                    {detect().name === 'ie' && <IEStyle />}

                    <ConnectedRouter history={this.props.history}>
                        <Route
                            render={({ location }) => {
                                return (
                                    <TransitionGroup
                                        style={{
                                            height: `${
                                                this.props.displayServiceFooter
                                                    ? `${height(false)}px`
                                                    : null
                                            }`
                                        }}
                                        component={TransitionGroupContainer}
                                        childFactory={childFactoryCreator(
                                            isPreviousActionTransition(
                                                this.props.pathname,
                                                this.props.activeAction.identifier,
                                                this.props
                                                    .lastInitiatedLocalRepIdentifier,
                                                this.props.emailStep2BackButtonClicked,
                                                this.props.showNextActionAnimation
                                            )
                                                ? 'backtransition'
                                                : 'transition'
                                        )}
                                    >
                                        <CssTransition
                                            key={this.props.animationIdentifier}
                                            classNames={
                                                isPreviousActionTransition(
                                                    this.props.pathname,
                                                    this.props.activeAction.identifier,
                                                    this.props
                                                        .lastInitiatedLocalRepIdentifier,
                                                    this.props
                                                        .emailStep2BackButtonClicked,
                                                    this.props.showNextActionAnimation
                                                )
                                                    ? 'backtransition'
                                                    : 'transition'
                                            }
                                            timeoutInMs={650}
                                        >
                                            <TransitionContainerNoExit
                                                isNextActionTransition={
                                                    !this.props.isNextActionTransition
                                                }
                                                noAnimation={
                                                    !this.props.showNextActionAnimation
                                                }
                                                zIndex={30}
                                            >
                                                <Switch location={location}>
                                                    <Redirect
                                                        from="/"
                                                        exact
                                                        to={this.props.defaultRoute}
                                                    />
                                                    <Route
                                                        path="/donation"
                                                        name="Donation"
                                                        component={Donation}
                                                    />
                                                    <Route
                                                        path="/poll"
                                                        name="Poll"
                                                        component={Poll}
                                                    />
                                                    <Route
                                                        path="/sentimentpoll"
                                                        name="SentimentPoll"
                                                        component={SentimentPoll}
                                                    />
                                                    <Route
                                                        path="/quiz"
                                                        name="Quiz"
                                                        component={Quiz}
                                                    />
                                                    <Route
                                                        path="/voteRegistration"
                                                        name="VoteRegistration"
                                                        component={VoteRegistration}
                                                    />
                                                    <Route
                                                        path="/electionReminder"
                                                        name="ElectionReminder"
                                                        component={ElectionReminder}
                                                    />
                                                    <Route
                                                        path="/findPollingStation"
                                                        name="FindPollingStation"
                                                        component={FindPollingStation}
                                                    />
                                                    <Route
                                                        path="/checkRegistration"
                                                        name="CheckRegistration"
                                                        component={CheckRegistration}
                                                    />
                                                    <Route
                                                        path="/call"
                                                        name="Call"
                                                        component={Call}
                                                    />
                                                    <Route
                                                        path="/email"
                                                        name="Email"
                                                        component={Email}
                                                    />
                                                    <Route
                                                        path="/link"
                                                        name="Link"
                                                        component={Link}
                                                    />
                                                    <Route
                                                        path="/video"
                                                        name="Video"
                                                        component={Video}
                                                    />
                                                    <Route
                                                        path="/speedometer"
                                                        name="RadialSlider"
                                                        component={RadialSlider}
                                                    />
                                                    <Route
                                                        path="/tweet"
                                                        name="Tweet"
                                                        component={Tweet}
                                                    />
                                                    <Route
                                                        path="/opinion"
                                                        name="Opinion"
                                                        component={Opinion}
                                                    />
                                                    <Route
                                                        path="/petition"
                                                        name="Petition"
                                                        component={Petition}
                                                    />
                                                    <Route
                                                        path="/preload"
                                                        component={PreloadScreen}
                                                    />
                                                    <Route
                                                        path="/checkrepposition"
                                                        name="RepStance"
                                                        component={RepStance}
                                                    />
                                                    <Route
                                                        path="/recordvideo"
                                                        name="RecordVideo"
                                                        component={RecordVideo}
                                                    />
                                                    <Route
                                                        path="/targetedMultiAction"
                                                        name="TargetedMultiAction"
                                                        component={TargetedMultiAction}
                                                    />
                                                    <Route
                                                        path="/multiActionsCard"
                                                        name="MultiActionsCard"
                                                        component={MultiActionsCard}
                                                    />
                                                    <Route
                                                        path="/actionChoiceCard"
                                                        name="ActionChoiceCard"
                                                        component={ActionChoiceCard}
                                                    />
                                                    <Route
                                                        path="/volunteer"
                                                        name="Volunteer"
                                                        component={Volunteer}
                                                    />
                                                </Switch>
                                            </TransitionContainerNoExit>
                                        </CssTransition>
                                        {this.props.displayServiceFooter && (
                                            <FooterNotice isPortrait={isPortrait()}>
                                                <span>
                                                    {`This service is operated by Speakable, PBC and subject to Speakable's `}
                                                    <a
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        href="https://actionbutton.org/terms"
                                                    >
                                                        {'terms of use'}
                                                    </a>
                                                    {' and '}
                                                    <a
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        href="https://actionbutton.org/privacy"
                                                    >
                                                        {'privacy statement'}
                                                    </a>
                                                    {'.'}
                                                </span>
                                            </FooterNotice>
                                        )}
                                    </TransitionGroup>
                                );
                            }}
                        />
                    </ConnectedRouter>
                    <TransitionGroup>
                        {isBounceableRoute(this.props.pathname) &&
                            !!this.props.bounce &&
                            !!this.props.engaged && (
                                <CssTransition
                                    key="alert"
                                    in={!!this.props.bounce && !!this.props.widgetViewed}
                                >
                                    <BounceModal />
                                </CssTransition>
                            )}
                    </TransitionGroup>
                    <TransitionGroup>
                        {this.props.bounceFormSuccess && (
                            <CssTransition key="alert" in={this.props.bounceFormSuccess}>
                                <ToasterAlert
                                    content={copyFor(
                                        'miscellaneous',
                                        'bounceFormSuccess',
                                        'general',
                                        capitalization.all
                                    )}
                                />
                            </CssTransition>
                        )}
                    </TransitionGroup>
                </WidgetContainer>
            </ThemeContext.Provider>
        );
    }
}

export default connect(
    state => {
        let animationIdentifier =
            state.widget.activeAction.identifier + state.widget.noCardsVisited;

        if (state.router.location != null) {
            const location =
                state.router.location.pathname === '/'
                    ? `/${getDefaultRoute(
                          state.widget.activeAction.type,
                          state.widget.cards[state.widget.activeAction.order - 1].type,
                          state.widget.activeAction.skipFrontCard ||
                              state.widget.activeAction.scrollableDesign ||
                              false
                      )}`
                    : state.router.location.pathname;
            if (location.indexOf('/start') !== -1) {
                if (location.indexOf('/volunteer') === -1) {
                    animationIdentifier = 'start' + animationIdentifier;
                }
            }
            if (location.indexOf('/targetedMultiAction') !== -1) {
                animationIdentifier = 'targetedMultiAction';
            }
            if (location.indexOf('/multiActionsCard') !== -1) {
                animationIdentifier = 'multiActionsCard';
            }
            if (location.indexOf('/actionChoiceCard') !== -1) {
                animationIdentifier = 'actionChoiceCard';
            }
        }

        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        const nextActionIsRepStance =
            (nextAction || {}).type === ActionTypes.CheckRepPosition;
        const currentPathname = state.router.location.pathname;

        return {
            activeAction: state.widget.activeAction,
            activeActionUsesLocalReps:
                !!state.widget.activeAction.representativeSearch ||
                state.widget.activeAction.type === ActionTypes.CheckRepPosition,
            address: state.form.google_autocomplete
                ? state.form.google_autocomplete.values
                : {},
            animationIdentifier: animationIdentifier,
            actionIdentifier: state.widget.activeAction.identifier,
            articleUrl: state.widget.articleUrl,
            bounceFormSuccess: state.widget.bounceFormSuccess,
            currentFormValues: !!findFormByPathname(currentPathname)
                ? (state.form[findFormByPathname(currentPathname)] || {}).values
                : {},
            defaultRoute: state.widget.savedRoute || `/${getDefaultRoute(
                state.widget.activeAction.type,
                state.widget.cards[state.widget.activeAction.order - 1].type,
                state.widget.activeAction.skipFrontCard ||
                    state.widget.activeAction.scrollableDesign ||
                    false
            )}`,
            savedRoute: state.widget.savedRoute,
            displayServiceFooter: state.widget.publisherId === 54, // MSN ID
            emailStep2BackButtonClicked: state.email.backButtonClicked,
            endUserLocation: state.widget.endUserLocation,
            loaded: state.widget.loaded,
            engaged: state.widget.engaged,
            widgetViewed: state.widget.widgetViewed,
            bounce: state.widget.bounce,
            facebookPixelIds: state.widget.facebookPixelIds,
            isLocalRepLoading: state.localRep.isLocalRepLoading,
            isNextActionTransition: state.widget.isNextActionTransition,
            lastInitiatedLocalRepIdentifier: state.widget.lastInitiatedLocalRepIdentifier,
            localRepFound: state.localRep.localRepData.representative !== undefined,
            maximumRepresentativeSearch: state.widget.maximumRepresentativeSearch,
            nextAction,
            nextActionUseLocalReps:
                !!(nextAction || {}).representativeSearch || nextActionIsRepStance,
            pathname:
                state.router.location != null ? state.router.location.pathname : null,
            locationKey: state.router.location != null ? state.router.location.key : null,
            showNextActionAnimation: state.widget.showNextActionAnimation,
            widgetRequestId: state.widget.widgetRequestId,
            themeData: {
                colors: {
                    primary: state.widget.theme.colors.primary || '#ff245e',
                    secondary: state.widget.theme.colors.secondary || '#072647'
                }
            },
            widgetId: state.widget.widgetId
        };
    },
    {
        loadLocalRep,
        resizeWidget,
        widgetBounce,
        widgetEngaged,
        widgetLoaded,
        widgetVisible
    }
)(Widget);
